// Phase 3 follow-up — accidental tenant-view drop notice.
//
// Problem: tenant-view selection is intentionally in-memory only (see
// context.tsx) — it is never persisted, so a hard navigation / reload
// silently resets a platform admin back to ALL-TENANTS platform scope.
// Today that looks IDENTICAL to a deliberate "Exit tenant view" click:
// the amber TenantSelectorBand banner just disappears. The operator has
// asked for the accidental case to be surfaced.
//
// Design: a breadcrumb, not a restore (per the operator's explicit
// direction, preserving the "never silently re-apply tenant scope"
// property):
//   - tenant-view-marker.ts writes a lightweight { public_id, name, code }
//     marker to sessionStorage whenever tenant view is entered/switched,
//     and clears it on explicit exit.
//   - On mount, if the marker exists but no tenant is currently selected,
//     that combination can ONLY mean the marker survived a reload/hard-nav
//     that reset the in-memory selection out from under the operator — an
//     explicit exit always clears the marker first. This component shows a
//     notice in that case, with "Return to <tenant>" (re-selects only on
//     an explicit click, after re-fetching the tenant canonically — never
//     automatically) and "Dismiss".
//
// Placement: rendered in the SAME slot as TenantSelectorBand's global
// amber banner (AppShell), immediately after it. The two are mutually
// exclusive (one needs selectedTenant truthy, this needs it falsy), so an
// operator who had the amber "Viewing as tenant: X" banner and then lands
// back on a freshly-loaded page sees THIS notice replace it in place —
// reads as "this is what happened to that banner" rather than a toast
// that can be missed while the operator's attention is on newly-loaded
// page content, not the notification corner. Toasts also auto-dismiss;
// this must stay up (persistent, dismissible) until the operator acts,
// since a silently-widened scope is a compliance-relevant state, not a
// transient event.
import { useState } from 'react'
import { AlertTriangle, LogIn } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useIsPlatformAdmin } from '@/lib/permissions'
import { usePlatformTenant } from '../context'
import { getTenant } from '../api'
import {
  readTenantViewMarker,
  clearTenantViewMarker,
  type TenantViewMarker,
} from '../tenant-view-marker'

export function TenantViewDroppedNotice() {
  const isPlatformAdmin = useIsPlatformAdmin()
  // Same gate as TenantSelectorBand — tenant view (and therefore its
  // breadcrumb) only ever exists for platform admins.
  if (!isPlatformAdmin) return null
  return <TenantViewDroppedNoticeInner />
}

function TenantViewDroppedNoticeInner() {
  const { selectedTenant, setSelectedTenant } = usePlatformTenant()
  // Read once at mount. This captures a breadcrumb left by the PREVIOUS
  // page life (reload / hard navigation) — it intentionally does not
  // re-read on every render. Once the operator returns, dismisses, or
  // (elsewhere) selects a different tenant, `selectedTenant` / `dismissed`
  // alone decide visibility from here on.
  const [marker] = useState<TenantViewMarker | null>(() => readTenantViewMarker())
  const [dismissed, setDismissed] = useState(false)
  const [returning, setReturning] = useState(false)
  const [returnError, setReturnError] = useState<string | null>(null)

  const visible = marker !== null && selectedTenant === null && !dismissed
  if (!visible || !marker) return null

  const handleDismiss = () => {
    clearTenantViewMarker()
    setDismissed(true)
  }

  const handleReturn = async () => {
    setReturning(true)
    setReturnError(null)
    try {
      // Re-fetch canonically rather than trusting the cached breadcrumb —
      // the tenant's status may have changed (suspended/archived) since
      // the marker was written, and the backend re-validates the caller's
      // JWT against this tenant regardless.
      const detail = await getTenant(marker.public_id)
      setSelectedTenant(detail)
    } catch {
      setReturnError(
        `"${marker.name}" is no longer available. It may have been suspended, archived, or removed.`,
      )
    } finally {
      setReturning(false)
    }
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="relative z-10 flex flex-wrap items-center gap-2 border-b border-amber-400 bg-amber-100 px-4 py-1.5 text-sm dark:border-amber-600 dark:bg-amber-950/60"
    >
      <AlertTriangle
        size={14}
        className="shrink-0 text-amber-800 dark:text-amber-300"
        aria-hidden
      />
      <span className="text-amber-900 dark:text-amber-100">
        {returnError ?? (
          <>
            Tenant view ended unexpectedly — you left{' '}
            <span className="font-semibold">{marker.name}</span> and are now viewing{' '}
            <span className="font-semibold">ALL TENANTS</span> (platform scope).
          </>
        )}
      </span>
      <div className="ml-auto flex items-center gap-1">
        {!returnError && (
          <Button
            size="sm"
            variant="ghost"
            className="gap-1 text-amber-800 hover:bg-amber-200 dark:text-amber-200 dark:hover:bg-amber-900/50"
            onClick={() => void handleReturn()}
            disabled={returning}
          >
            <LogIn size={11} aria-hidden />
            {returning ? 'Returning…' : `Return to ${marker.name}`}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={handleDismiss}>
          Dismiss
        </Button>
      </div>
    </div>
  )
}
